// utils/playbackGuard.js
// ─────────────────────────────────────────────────────────────────────────────
// Client-side, HEURISTIC "is it safe to hand out the video URL?" checks.
//
// READ ME — limitations (by browser design, not laziness):
//   • A web page CANNOT enumerate installed extensions or read their names, so
//     we cannot detect a "video downloader" extension by name. Impossible.
//   • Video-downloader extensions don't announce themselves → undetectable.
//   • We CAN heuristically detect (a) that *an* ad/content blocker is active
//     (bait-element method) and (b) that devtools is probably open
//     (window-gap + console-getter probes).
//   • Everything here is bypassable and can false-positive. Treat it as a
//     DETERRENT only. Real protection = short-lived presigned URLs +
//     watermark + desktop-app / DRM gating.
// ─────────────────────────────────────────────────────────────────────────────

// Tune behaviour here. Ad-blockers are very common; set blockOnAdBlocker:false
// to only warn (recommended) instead of blocking playback.
export const GUARD_CONFIG = {
  blockOnAdBlocker: true,
  blockOnDevtools: true,
  // The window outer/inner size gap is an UNRELIABLE devtools signal — normal
  // browser chrome (tabs + address + bookmarks bar), zoom and OS display
  // scaling already exceed the threshold, causing false positives even with no
  // devtools open. Off by default; the tab-scoped console probe is used instead.
  useSizeHeuristic: false,
  devtoolsSizeThreshold: 200,
};

/**
 * Detect a cosmetic ad/content blocker via a bait element. Resolves true when
 * the bait gets hidden/removed (i.e. a blocker is filtering it).
 */
export function detectAdBlocker() {
  return new Promise((resolve) => {
    if (typeof document === "undefined") return resolve(false);
    try {
      const bait = document.createElement("div");
      bait.className =
        "pub_300x250 pub_300x250m pub_728x90 text-ad textAd text_ad " +
        "text-ads text-ad-links ad-banner adsbox ad-placement ads ad";
      bait.style.cssText =
        "position:absolute!important;left:-10000px!important;top:-10000px!important;" +
        "width:1px!important;height:1px!important;pointer-events:none!important;";
      bait.setAttribute("aria-hidden", "true");
      document.body.appendChild(bait);

      window.setTimeout(() => {
        let blocked = false;
        try {
          const cs = window.getComputedStyle(bait);
          blocked =
            bait.offsetParent === null ||
            bait.offsetHeight === 0 ||
            bait.clientHeight === 0 ||
            cs.display === "none" ||
            cs.visibility === "hidden";
        } catch { /* ignore */ }
        bait.remove();
        resolve(!!blocked);
      }, 130);
    } catch {
      resolve(false);
    }
  });
}

/**
 * Best-effort guess that devtools is open.
 *  1) Docked devtools widen the outer/inner window gap.
 *  2) A getter that only fires when the console renders the probe object.
 * Both are heuristics — undocked devtools and zoom can fool them.
 */
export function isDevtoolsOpen() {
  if (typeof window === "undefined") return false;

  if (!GUARD_CONFIG.useSizeHeuristic) return false;

  const threshold = GUARD_CONFIG.devtoolsSizeThreshold;

  return (
    window.outerWidth - window.innerWidth > threshold ||
    window.outerHeight - window.innerHeight > threshold
  );
}

/**
 * Run all checks. Returns { safe, reasons:[], adBlocker, devtools }.
 * `reasons` only includes a flag when its block toggle is enabled.
 */
export async function runPlaybackSafetyCheck() {
  let adBlocker = false;
  try { adBlocker = await detectAdBlocker(); } catch { /* ignore */ }
  const devtools = isDevtoolsOpen();

  const reasons = [];
  if (GUARD_CONFIG.blockOnAdBlocker && adBlocker) reasons.push("ad-blocker");
  if (GUARD_CONFIG.blockOnDevtools && devtools) reasons.push("devtools");

  return { safe: reasons.length === 0, reasons, adBlocker, devtools };
}
